import React from "react";
import { FiActivity, FiPlus } from 'react-icons/fi';
import { useFormModal } from "@core/hooks/useFormModal";
import { FormModal, FormModalField } from "@core/components/FormModal";
import { useActivityAction } from "@features/admin/activity/hooks/useActivityAction";
import { CreateActivityRequest } from "@features/admin/activity/types/activityRequests";
import { useScopeId } from "@core/hooks/useRouteParamId";

const FIELDS: FormModalField<CreateActivityRequest>[] = [
    {
        name: "name",
        label: "Activity Name",
        type: "text",
        required: true,
        placeholder: "Enter Activity name",
        helpText: "A short name for this activity, e.g. Sunday Service or Youth Meeting.",
    },
    {
        name: "description",
        label: "Description",
        type: "textarea",
        placeholder: "Enter Activity description",
        helpText: "Optional details about what this activity is and how it is used in reports.",
    },
];

const CreateActivityModal: React.FC<{ show: boolean; onHide: () => void; onChanged?: () => void }> = ({ show, onHide, onChanged }) => {
    const resolvedNationId = useScopeId() ?? 0;
    const { doActivityCreate } = useActivityAction();

    const { form, formErrors, busy, handleChange, setFieldValue, handleSubmit } =
        useFormModal<CreateActivityRequest>({
            initial: { name: "", description: "" },
            validate: (f) => {
                const errors: Partial<Record<keyof CreateActivityRequest, string[]>> = {};
                const messages: string[] = [];
                if (!f.name?.trim()) {
                    errors.name = ["Activity name is required."];
                    messages.push("Activity name is required.");
                }
                return { errors, messages };
            },
            submit: (f) => doActivityCreate(resolvedNationId, f),
            onSuccess: () => {
                onHide();
                onChanged?.();
            },
            errorFallback: "Failed to create activity",
        });

    return (
        <FormModal<CreateActivityRequest>
            show={show}
            onHide={onHide}
            title="Create New Activity"
            icon={<FiActivity aria-hidden />}
            fields={FIELDS}
            form={form}
            formErrors={formErrors}
            busy={busy}
            onChange={handleChange}
            setFieldValue={setFieldValue}
            onSubmit={handleSubmit}
            submitLabel="Create Activity"
            busyLabel="Creating Activity..."
            submitIcon={<FiPlus className="me-2" />}
            testId="create-activity-modal"
        />
    );
};

export default CreateActivityModal;
